import React, { Component } from 'react';
import log from 'loglevel';
import styles from './CardInfo.module.scss';
import './CardInfo.scss';
import Typography from '@material-ui/core/Typography';
import {Card, CardActions, CardContent, CardHeader, CardMedia,Button, ButtonGroup} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CancelIcon from '@material-ui/icons/Cancel';
import Phone from "@material-ui/icons/Phone";
import DirectionsIcon from "@material-ui/icons/Directions";
import WarningIcon from '@material-ui/icons/Warning';
import LockIcon from '@material-ui/icons/Lock';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Servicio from "./Servicio";
import i18n from "../i18n";
import UrlService from "../common/UrlService";
import unescape from 'lodash/unescape';
import Swipe from "react-easy-swipe";
import CardData from "../data/CardData";
import {dataTypes} from "../common/Constants";
import AnalyticsAPIService from "../common/AnalyticsAPIService";
import ExternalHTMLContent from "./ExternalHTMLContent";
import MapService from "../common/MapService";
import {DirectionService} from "../common/DirectionService";
import {InfoOutlined} from "@material-ui/icons";
import POIData from "../data/POIData";
import {LocaleService} from "../common/LocaleService";
import StageCard from "../data/StageCard";
import HotelCard from "../data/HotelCard";
import PlaceCard from "../data/PlaceCard";
import OfflineCard from "../data/OfflineCard";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import AppService from "../common/AppService";
import type { ReactNode } from 'react';
import type { SwipePosition } from "react-easy-swipe";
import type { ServicioData } from './Servicio';
import type PlaceData from '../data/PlaceData';

/** Eventos que una card puede emitir hacia MapComponent. */
export type CardEventType = 'select' | 'next' | 'prev';

/** Callbacks que MapComponent entrega a la card. */
export interface CardEvents {
    cancelClick: () => void;
    handleCardEvent: (eventType: CardEventType, cardData: DetailCard) => void;
}

/**
 * Card que muestra CardInfo.
 *
 * El componente es polimorfico: recibe cualquiera de las subclases de CardData
 * y decide que pintar segun `type` en tiempo de ejecucion, sin estrechar el
 * tipo. Lo que anaden las subclases se declara aqui como opcional.
 */
export interface DetailCard extends CardData {
    /** OfflineCard. */
    offline?: boolean;
    /** PoiCard. */
    poiTypeReference?: string;
    /** HotelCard. */
    getRatingColorbarDOM?: () => ReactNode;
    /** OfflineCard: feature original, para recargar la card al reconectar. */
    featureData?: PlaceData;
}

export interface CardInfoProps {
    /** 'true' / 'false' como cadena, no booleano. */
    show: string;
    title?: string;
    disabledClass?: string;
    cardData?: DetailCard;
    cardEvents?: CardEvents;
    children?: ReactNode;
}

interface CardInfoState {
    expanded: boolean;
    swipping: boolean;
    fullWidthImage: boolean;
}

/**
 * Helper classes / components.
 */
class BookingButton extends React.Component<{ url: string }> {
    constructor(props: { url: string }) {
      super(props);
      this.openBookingLink = this.openBookingLink.bind(this);
    }

    openBookingLink(): void {
        const url = this.props.url;
        log.debug('CardInfo.openBookingLink', url);
        UrlService.openExternalUrl(url);
        AnalyticsAPIService.sendOutboundLink(
            'Booking.com',
            url
        );
    }

    render() { return (
        <Button variant="text" size="small" onClick={this.openBookingLink}  className={styles.bookingButton}>
             Booking
        </Button>
    )}
}

class PhoneButton extends Component<{ phoneNumber: string }> {
    constructor(props: { phoneNumber: string }) {
      super(props);
      this.phoneButtonCall = this.phoneButtonCall.bind(this);
    }

    phoneButtonCall(): void {
      const url = 'tel:' + this.props.phoneNumber;
      log.debug('CardInfo.phoneButtonCall', url);
      UrlService.openExternalUrl(url);
        AnalyticsAPIService.sendOutboundLink(
            'phone button',
            url
        );
    }

    render() { return (
        <Button  variant="text" size="small" color="secondary" className={styles.phoneButton} onClick={this.phoneButtonCall} >
           <Phone color="secondary" className="phone-icon"/>
        </Button>
    )}
}

class GronzeButton extends Component<{ url: string }> {
    constructor(props: { url: string }) {
      super(props);
      this.gotoGronzeLink = this.gotoGronzeLink.bind(this);
    }

    gotoGronzeLink(): void {
        const url = this.props.url;
        log.debug('CardInfo.gotoGronzeLink', url);
        UrlService.openInfo(LocaleService.translateGronzeLink(url));
        AnalyticsAPIService.sendOutboundLink(
            i18n.t('detailCard.goGronze'),
            url
        );
    }

    render() { return (
        <Button
            variant="text"
            size="small"
            color="secondary"
            className={styles.gronzeButton}
            onClick={this.gotoGronzeLink}
            endIcon={<OpenInNewIcon className={styles.openExternalIcon} />}
        >
             { i18n.t('detailCard.goGronze') }
        </Button>
    )}
}

interface DirectionButtonProps {
    /** El geofield de Drupal envia las coordenadas como cadena. */
    destination: { lat: string | number; lon: string | number };
    closeHandler: () => void;
}

class DirectionButton extends Component<DirectionButtonProps> {
    constructor(props: DirectionButtonProps) {
        super(props);
        this.createDirection = this.createDirection.bind(this);
    }

    async createDirection(): Promise<void> {
        await DirectionService.gotToDestination(this.props.destination);
        this.props.closeHandler();
    }

    render() { return (
        <Button  variant="text" size="medium" className={styles.directionButton} onClick={this.createDirection} >
            <DirectionsIcon fontSize="large" className="direction-icon"/>
        </Button>
    )}
}

interface CardImageHtmlProps {
    imgHtml?: string | null;
    imgUrl?: string | null;
    fit: string;
}

class CardImageHtml extends Component<CardImageHtmlProps> {
    render() {
        const { imgHtml, imgUrl, fit } = this.props;
        const fitClass = fit === 'cover' ? styles.coverImage : styles.containImage;

        return (
            <CardMedia className={`${styles.cardMedia} ${fitClass}`}>
                {imgUrl
                    ? <img src={imgUrl} alt="" />
                    : imgHtml
                        ? <span dangerouslySetInnerHTML={{ __html: imgHtml }} />
                        : null}
            </CardMedia>
        );
    }
}

/**
 * Component: Select card
 *  - cardId
 *  - isSelected
 */
interface SelectButtonProps {
    cardId?: number | string;
    isSelected?: boolean;
    handleCardEvent: (eventType: CardEventType, event?: React.MouseEvent) => void;
}

class SelectButton extends Component<SelectButtonProps, { selected: boolean }> {
    constructor(props: SelectButtonProps) {
      super(props);

      this.clickEvent = this.clickEvent.bind(this);
      this.state = {
        selected: false,
      }
    }

    clickEvent(event: React.MouseEvent): void {
      this.props.handleCardEvent(CardInfo.EVENT_TYPE_SELECT, event);
      this.setState({
          selected: !this.state.selected,
      });
    }

    componentDidUpdate(prevProps: SelectButtonProps): void {
      // Refresh state only when component changes.
      if (this.props.cardId !== prevProps.cardId) {
        this.setState({selected: !!this.props.isSelected});
      }
    }

    render() {
      const selected = this.state.selected;
      // @todo Make text more generic, for other card types.
      const text = selected ? i18n.t('stage.selected') : i18n.t('stage.unselected');
      const selectedStyle = selected ? "secondary" : "primary";

      return (
        <Button variant="contained"
                size="small"
                color={selectedStyle}
                onClick={ this.clickEvent }
                className={styles.selectButton}
        >
            {text}
        </Button>
      )
    }
}


interface NavButtonProps {
    handleClick: (event: React.MouseEvent) => void;
    disabled?: boolean;
}

class PrevButton extends Component<NavButtonProps> {
    render() { return (
        <Button  variant="text" size="small" color="secondary"
          className={styles.prevButton} onClick={this.props.handleClick}
          disabled={this.props.disabled}>
            <span >{i18n.t('stage.previous')}</span>&nbsp;<span className={styles.mediumSizeIcon + " glyphicons glyphicons-chevron-right" }/>
        </Button>
    )}
}

class NextButton extends Component<NavButtonProps> {
    render() { return (
        <Button  variant="text" size="small" color="secondary"
          className={styles.nextButton}
          onClick={this.props.handleClick}
          disabled={this.props.disabled}>
            <span className={styles.mediumSizeIcon + " glyphicons glyphicons-chevron-left" }/><span >{i18n.t('stage.next')}</span>
        </Button>
    )}
}

/**
 * Button group. Props:
 * - cardData
 * - handleShowNext
 * - hanbleShowNext
 * - handleSelectCard
 */
interface CardSelectButtonsProps {
    cardData: DetailCard;
    handleCardEvent: (eventType: CardEventType, event?: React.MouseEvent) => void;
}

class CardSelectButtons extends Component<CardSelectButtonsProps> {
    constructor(props: CardSelectButtonsProps) {
      super(props);

      // click to get next and prev
      this.handleShowPrev = this.handleShowPrev.bind(this);
      this.handleShowNext = this.handleShowNext.bind(this);
    }

        // handle go to prev stage button
    handleShowPrev(event: React.MouseEvent): void {
        this.props.handleCardEvent(CardInfo.EVENT_TYPE_PREV, event);
    }

    // handle go to next stage button
    handleShowNext(event: React.MouseEvent): void {
        this.props.handleCardEvent(CardInfo.EVENT_TYPE_NEXT, event);
    }

    render() {
        const card = this.props.cardData;
        return (
            <ButtonGroup disableElevation variant="contained" fullWidth className={styles.selectButtonsBox}>
                <NextButton handleClick={this.handleShowNext} disabled={ !card.hasNext() } />
                {card.canSelect() &&
                        <SelectButton
                           cardId={card.id} // Force card refreshing
                           handleCardEvent={ this.props.handleCardEvent }
                           isSelected={ card.selected } />
                }
                <PrevButton handleClick={this.handleShowPrev} disabled={ !card.hasPrev() }/>
            </ButtonGroup>
        )
    }
}

class InfoButton extends Component<{ url: string; title: string }> {
    render() { return (
        <div className={styles.poiUrlLink} onClick={() => { UrlService.openExternalUrl(this.props.url) }}>
            <InfoOutlined />
            <span>{this.props.title}</span>
        </div>
    )}
}

/**
 * Basic content card.
 *
 * Expanding functionality:
 * https://stackoverflow.com/questions/59938274/expanding-individual-cards-with-react-material-ui-card-expander
 */
class CardInfo extends Component<CardInfoProps, CardInfoState> {

     // minimun swipe movement in pixels
     MIN_SWIPE_SIZE = 40;

     // Define some event types for cards.
     static EVENT_TYPE_SELECT: CardEventType = 'select';
     static EVENT_TYPE_NEXT: CardEventType = 'next';
     static EVENT_TYPE_PREV: CardEventType = 'prev';

     static RESPONSIVE_IMAGE_TYPES: string[] = [dataTypes.place, dataTypes.poi, dataTypes.hotel];

     swipping = false;

     /** Listener de cambio de orientacion, creado en componentDidMount. */
     orientationListener: MediaQueryList | undefined;

     /**
      * Contenedores con scroll: en horizontal desplaza la card entera y en
      * vertical expandida solo el contenido.
      */
     cardRef = React.createRef<HTMLDivElement>();
     cardContentRef = React.createRef<HTMLDivElement>();

     /**
      * Al ir a la etapa anterior / siguiente la card no se desmonta, solo
      * cambian sus datos, y el DOM conservaria el scroll de la etapa previa.
      */
     resetScrollOnNewCard = false;

    constructor(props: CardInfoProps) {
        super(props);
        log.debug('props.cardData', props.cardData);

        this.state = {
            expanded: false,
            swipping: false,
            fullWidthImage: false,
        }

        // Action Buttons
        this.closeHandler = this.closeHandler.bind(this);
        this.toggleHandler = this.toggleHandler.bind(this);


        // Other controls
        this.handleExpandClick = this.handleExpandClick.bind(this);
        this.handleCardEvent = this.handleCardEvent.bind(this);

        // swipe handlers
        this.handleSwipeStart = this.handleSwipeStart.bind(this);
        this.handleSwipeMove = this.handleSwipeMove.bind(this);
        this.handleSwipeEnd = this.handleSwipeEnd.bind(this);

        // scroll bottom control
        this.handleScroll = this.handleScroll.bind(this);

        // stage image
        this.toggleFullWidthImage = this.toggleFullWidthImage.bind(this);
    }

    componentDidMount(): void {
        // Detecta cambio de orientación
        this.orientationListener = window.matchMedia("(orientation: landscape)");
        this.orientationListener.addEventListener("change", this.handleOrientationChange);
    }

    componentWillUnmount(): void {
        // Limpia el listener al desmontar el componente
        if (this.orientationListener) {
            this.orientationListener.removeEventListener("change", this.handleOrientationChange);
        }
    }

    handleOrientationChange = (event: MediaQueryListEvent): void => {
        if (event.matches) { // si ahora está en horizontal
            this.setState({ expanded: false });
        }
    };

    // Unused atm
    handleExpandClick = () => {
      log.debug("Expand Click");
        this.setState({expanded: !this.state.expanded})
    };

    closeHandler(): void {
        this.setState({expanded: false});
        this.props.cardEvents!.cancelClick();
    }

    toggleHandler(): void {
        const expanded = !this.state.expanded;
        this.setState({ expanded });
    }

    /**
     * Swipe handlers (See SwipeableBox)
     */
    handleSwipeStart(): void {
        if (AppService.isLandscape())
            return;
        this.swipping = !this.state.fullWidthImage;
    }

    handleSwipeMove(position: SwipePosition): void {
        if (AppService.isLandscape())
            return;

        if (!this.swipping) return;

        if (position.y > this.MIN_SWIPE_SIZE) {
            if (this.state.expanded) {
                log.debug("Swipe down (Unexpand)");
                this.setState({
                    expanded: false,
                });
                this.swipping = false;
            } else {
                log.debug("Swipe down (Close)");
                this.swipping = false;
                this.closeHandler();
            }
        } else  if (position.y < -this.MIN_SWIPE_SIZE) {
            log.debug("Swipe up (Expand)");
            this.setState({
                expanded: true,
            });
            this.swipping = false;
        } else if (position.x > this.MIN_SWIPE_SIZE) {
            if (this.props.cardData!.type === dataTypes.stage && this.props.cardData!.hasNext()) {
                this.handleCardEvent(CardInfo.EVENT_TYPE_NEXT);
            }
        } else if (position.x < -this.MIN_SWIPE_SIZE) {
            if (this.props.cardData!.type === dataTypes.stage && this.props.cardData!.hasPrev()) {
                this.handleCardEvent(CardInfo.EVENT_TYPE_PREV);
            }
        }

    }

    handleSwipeEnd(): void {
        if (AppService.isLandscape())
            return;
        
        this.swipping = false;
    }

    handleScroll(event: React.TouchEvent<HTMLElement>): void {
        const { currentTarget } = event;
        if (currentTarget.scrollHeight - currentTarget.offsetHeight > 0 &&
            currentTarget.scrollTop !== 0) {
            event.stopPropagation();
        }
    }

    /**
     * We need this intermediate handler to be able to pass on current card
     * to MapComponent handler.
     */
    async handleCardEvent(eventType: CardEventType, event?: React.MouseEvent): Promise<void> {
        if (event) event.stopPropagation();
        // Los dos caminos que llegan aqui exigen carta: el swipe la comprueba y
        // CardSelectButtons solo se pinta bajo cardData.hasSelectionButtons().
        // El que se renderiza sin cardData ni cardEvents es el fallback
        // "Empty card", que no monta ninguno de los dos.
        const { cardData, cardEvents } = this.props;
        if (!cardData || !cardEvents) return;
        if (eventType === CardInfo.EVENT_TYPE_NEXT || eventType === CardInfo.EVENT_TYPE_PREV) {
            this.resetScrollOnNewCard = true;
        }
        await MapService.zoomToStageLevel();
        cardEvents.handleCardEvent(eventType, cardData);
    }

    componentDidUpdate(prevProps: CardInfoProps): void {
        if (prevProps.show === 'false' && this.props.show === 'true' ||
            prevProps.title !== this.props.title) {
            const {cardType, title, url} = this.getTitles();
            this.sendAnalyticsOpenCardEvent(`[${cardType}] ${title}`, url);
        }
        // La etapa nueva llega de forma asincrona: se resetea al recibirla,
        // no al pulsar, para no dejar la anterior arriba mientras carga.
        if (this.resetScrollOnNewCard && prevProps.cardData !== this.props.cardData) {
            this.resetScrollOnNewCard = false;
            this.resetScroll();
        }
    }

    resetScroll(): void {
        // 'instant': en horizontal la card tiene scroll-behavior: smooth.
        for (const element of [this.cardRef.current, this.cardContentRef.current]) {
            element?.scrollTo({ top: 0, behavior: 'instant' });
        }
    }

    getTitles(): { cardType: string; title: string; url: string | null | undefined } {
        const title = unescape(this.props.title || this.props.cardData!.getTitle()).replace('&#039;', "'");
        const url = this.props.cardData? this.props.cardData.getUrl() : '';
        const cardType = this.props.cardData? this.props.cardData.getType() : '';
        return {cardType, title, url};
    }

    /**
     * Resolves the responsive image to show on the card.
     *
     * Returns the image url plus how it should be fitted into its ratio box
     * (see CardInfo.module.scss): the 4:3 / 16:9 boxes are enforced by CSS,
     * while `fit` decides whether the image is shown complete ('contain') or
     * cropped & centered ('cover', only for booking images).
     */
    getCardImage(): { url?: string | null; fit: string } {
        const cardData = this.props.cardData;
        const expanded = this.state.expanded;
        const isLandscape = AppService.isLandscape();
        const isResponsiveImage = CardInfo.RESPONSIVE_IMAGE_TYPES.includes(cardData?.type as string);
        const imageStyles = cardData?.data?.image_main?.image_styles;

        const responsiveStyleKey = isLandscape || !expanded
            ? 'wide_640'
            : (cardData?.type === dataTypes.poi ? 'width_940' : 'rect_940');

        if (cardData?.type === dataTypes.hotel) {
            const bookingImage = cardData?.data?.image_main?.image_source === 'booking';
            if (bookingImage) {
                return { url: imageStyles?.booking_original, fit: 'cover' };
            }
            return { url: imageStyles?.[responsiveStyleKey], fit: 'contain' };
        }
        if (isResponsiveImage) {
            return { url: imageStyles?.[responsiveStyleKey], fit: 'contain' };
        }
        return { url: null, fit: 'contain' };
    }

    async sendAnalyticsOpenCardEvent(title: string, url?: string | null): Promise<void> {
        await AnalyticsAPIService.sendViewCard(
            title,
            url as string,
        );
    }

    toggleFullWidthImage(): void {
        this.setState({fullWidthImage: !this.state.fullWidthImage});
    }

    render() {
        const expanded = this.state.expanded;
        const cardData = this.props.cardData;

        // Card class will depend on whether we've got image or not
        const cardClass = [styles.detailCard];
        if (this.props.show === 'true') {
            cardClass.push(styles.show);
            cardClass.push('show-card');
            if (expanded) {
                cardClass.push(styles.expanded);
                cardClass.push('expanded');
            }
        } else {
            cardClass.push(styles.hide);
        }

        if (this.props.cardData) {
            switch (this.props.cardData.type) {
                case dataTypes.hotel:
                    cardClass.push(styles.hotel);
                    cardClass.push('hotel');
                    cardClass.push(styles.responsiveImage);
                    break;
                case dataTypes.stage:
                    cardClass.push('stage');
                    break;
                case dataTypes.route:
                    cardClass.push('route');
                    break;
                case dataTypes.poi:
                    cardClass.push('poicard');
                    cardClass.push(styles.responsiveImage);
                    break;
                case dataTypes.place:
                    cardClass.push(styles.responsiveImage);
                    break;
            }
        }

        if (cardData) {
            if (cardData.getDetailCardSize() === CardData.BIG) {
              cardClass.push(styles.imageCard) ;
            }
        }

        if (cardData?.offline) {
            cardClass.push(styles.offlineCard);
        }

        // filter html entities from title
        const {title} = this.getTitles();
        const warningText = cardData ? cardData.getWarningText() : '';
        const warningType = cardData && warningText ? cardData.getWarningType() : null;

        const { url: cardImageUrl, fit: cardImageFit } = this.getCardImage();
        const cardImageHtml = cardImageUrl ? null : cardData?.imgHTML;

        // route name on stage cards
        const routeName = cardData?.type === 'stage' && !cardData.offline ? (cardData.data!.route_ref as { name: string }).name : null;

        let cardContent;
        if (cardData?.offline) {
            // Offline: detail data is not available, just show the message.
            cardContent = OfflineCard.getCardDOM();
        } else if (cardData?.type === dataTypes.stage) {
            cardContent = StageCard.getCardDOM(cardData as StageCard);
        } else if (cardData?.type === dataTypes.hotel) {
            cardContent = HotelCard.getCardDOM(cardData as HotelCard);
        } else if (cardData?.type === dataTypes.place) {
            cardContent = PlaceCard.getCardDOM(cardData as PlaceCard);
        }

        const stageImageClasses = this.state.fullWidthImage ?
            styles.elevationImage + " " + styles.fullWidth + " full-width-image" : styles.elevationImage;

        return (
        <div className={styles.cardContainer}>
            {cardData &&
            <Card className={cardClass.join(' ')} ref={this.cardRef}>

                <div className="swype-line" onClick={this.toggleHandler}/>
                <Swipe
                    onSwipeStart={this.handleSwipeStart}
                    onSwipeMove={this.handleSwipeMove}
                    onSwipeEnd={this.handleSwipeEnd}
                    className={styles.cardContentWrapper + ' ' + (this.state.fullWidthImage ? styles.fullWidthImage : '')}
                >
                    <div className="cancel-icon card">
                        <CancelIcon color="action" onClick={this.closeHandler} />
                    </div>

                    {
                        <Typography className={styles.routeName} variant="h5" component="h5" gutterBottom>
                            { routeName }
                        </Typography>
                    }
                    <CardHeader
                        title={
                            <Typography className={styles.placeTitle} variant="h5" component="h5" gutterBottom>
                                {title}
                            </Typography>
                        }
                    >
                    </CardHeader>

                    {
                        POIData.haveRating(cardData.poiTypeReference) && cardData.rating ?
                            <div className="card-rating">
                                <ExternalHTMLContent contentHtml={POIData.createStars(cardData.rating.value)} />
                            </div>
                            :
                            null
                    }

                    {(cardData.type === dataTypes.stage || cardData.type === dataTypes.route || cardData.type === dataTypes.hotel) && warningText &&
                        <Alert 
                            severity="warning"
                            className={`${styles.notaDestacada} ${cardData.type === dataTypes.route ? styles.route : ''} ${cardData.type === dataTypes.hotel ? styles.hotel : ''}`}
                            icon={warningType === CardData.WARNING_CLOSED ? <LockIcon /> : <WarningIcon />}
                        >
                            {warningText}
                        </Alert>
                    }

                    { ((cardData.type === dataTypes.hotel || cardData.type === dataTypes.poi || cardData.type === dataTypes.place) &&
                            (cardImageUrl || cardImageHtml)) &&
                        <CardImageHtml
                            imgUrl={cardImageUrl}
                            imgHtml={cardImageHtml}
                            fit={cardImageFit}
                        />
                    }

                    { (cardData.type === dataTypes.stage && cardData.elevationImage) &&
                        <div className={stageImageClasses} onClick={this.toggleFullWidthImage}>
                            <img
                                src={cardData.elevationImage}
                                alt={i18n.t('cardContent.stage.elevation')}
                            />
                            <div className={styles.zoomIcon + " zoom-icon"} >
                                {
                                    this.state.fullWidthImage ?
                                        <ZoomOutIcon />
                                        :
                                        <ZoomInIcon />
                                }
                            </div>
                        </div>
                    }

                    { cardData.hasSelectionButtons() &&
                        <CardSelectButtons handleCardEvent={ this.handleCardEvent } cardData={ cardData }/>
                    }
                    <CardContent className={styles.cardContent} onTouchStart={this.handleScroll} ref={this.cardContentRef}>
                        <div className="card-content">
                            <div className={styles.infoContainer}>
                                {cardData.tag_servicios && cardData.type !== dataTypes.hotel &&
                                <div className='servicios-container'>
                                     {cardData.tag_servicios.map((servicio: ServicioData, index: number) => <Servicio data={servicio}  key={index}/>)}
                                </div>
                                }
                                {
                                    cardData.offline ?
                                        cardContent
                                        :
                                    cardData.type === dataTypes.stage ?
                                        cardContent
                                        :
                                        cardData.type === dataTypes.hotel ?
                                            <>
                                                {cardData.getRatingColorbarDOM!()}
                                                {cardData.tag_servicios &&
                                                <div className='servicios-container'>
                                                     {cardData.tag_servicios.map((servicio: ServicioData, index: number) => <Servicio data={servicio}  key={index}/>)}
                                                </div>
                                                }
                                                {
                                                    cardContent ?
                                                        <>
                                                            {cardContent}
                                                        </>
                                                        :
                                                        <>
                                                            {cardData.extra_html && <ExternalHTMLContent contentHtml={cardData.extra_html} />}
                                                        </>
                                                }
                                            </>
                                            :
                                            cardData.type === dataTypes.place ?
                                                <>{cardContent}</>
                                                :
                                                cardData.extra_html && <ExternalHTMLContent contentHtml={cardData.extra_html} />

                                }
                                {
                                    cardData.poiUrl && cardData.poiUrl.url && cardData.poiUrl.title ?
                                    <InfoButton url={cardData.poiUrl.url} title={cardData.poiUrl.title} />
                                    :
                                    null
                                }
                            </div>
                        </div>
                    </CardContent>
                </Swipe>

                { !cardData.offline &&
                <CardActions className={styles.cardActions + ' card-actions'}>
                    <div className={styles.actionsContainer}>
                        { (cardData?.type === dataTypes.hotel  || cardData.type === dataTypes.poi) &&
                            <DirectionButton
                                destination={{lat: cardData.geo_location!.lat, lon: cardData.geo_location!.lon} }
                                closeHandler={this.closeHandler}
                            ></DirectionButton>
                        }
                        { cardData.phoneNumber &&  <PhoneButton phoneNumber={cardData.phoneNumber} /> }
                        { cardData.bookingUrl &&   <BookingButton url={cardData.bookingUrl} /> }
                        { cardData.link &&         <GronzeButton url={cardData.link} /> }
                    </div>
                </CardActions>
                }
            </Card>
            }
      </div>
      );
    }

}

export default CardInfo;
